import React, { useState, useEffect, useMemo } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  CircularProgress, 
  Alert,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { 
  Chart as ChartJS, 
  CategoryScale, 
  LinearScale, 
  BarElement,
  Title, 
  Tooltip, 
  Legend,
  ArcElement
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { getTestExecutions } from '../../services/testService';
import { getAllFixVersions } from '../../services/apiService';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  ChartDataLabels
);

/**
 * ReleaseComparisonCharts component for displaying comparison charts for releases
 * @returns {JSX.Element} ReleaseComparisonCharts component
 */
const ReleaseComparisonCharts = () => {
  // State for releases and test data
  const [releases, setReleases] = useState([]);
  const [testData, setTestData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Fetch releases and test data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch all releases
        const releasesResponse = await getAllFixVersions();
        
        let releasesData = [];
        
        // Process releases response based on format
        if (releasesResponse && releasesResponse.data && Array.isArray(releasesResponse.data)) {
          releasesData = releasesResponse.data;
        } else if (releasesResponse && Array.isArray(releasesResponse)) {
          releasesData = releasesResponse;
        } else if (releasesResponse && releasesResponse.data && releasesResponse.data.data && Array.isArray(releasesResponse.data.data)) {
          releasesData = releasesResponse.data.data;
        } else if (releasesResponse && releasesResponse.success && Array.isArray(releasesResponse.data)) {
          releasesData = releasesResponse.data;
        } else {
          throw new Error('Invalid releases response format');
        }
        
        // Filter active releases and sort by version
        const activeReleases = releasesData
          .filter(release => release.active)
          .sort((a, b) => {
            // Parse version strings like 5.9.08
            const versionA = a.version.split('.').map(Number);
            const versionB = b.version.split('.').map(Number);
            
            // Compare major version
            if (versionA[0] !== versionB[0]) return versionA[0] - versionB[0];
            // Compare minor version
            if (versionA[1] !== versionB[1]) return versionA[1] - versionB[1];
            // Compare patch version
            return versionA[2] - versionB[2];
          });
        
        // Get the last 10 releases
        const last10Releases = activeReleases.slice(-10);
        
        setReleases(last10Releases);
        
        // Fetch test data for each release
        const testDataMap = {};
        
        for (const release of last10Releases) {
          try {
            const testResponse = await getTestExecutions(release.version);
            
            if (testResponse && testResponse.data) {
              testDataMap[release.version] = testResponse.data;
            }
          } catch (err) {
            console.error(`Error fetching test data for release ${release.version}:`, err);
            // Continue with other releases even if one fails
          }
        }
        
        setTestData(testDataMap);
      } catch (err) {
        console.error('Error fetching data for comparison charts:', err);
        setError('Failed to load data for comparison charts. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  // Prepare chart data for execution status
  const prepareExecutionStatusData = () => {
    const labels = releases.map(release => release.version);
    
    // Get all possible outcomes
    const allTests = Object.values(testData).flat();
    const uniqueOutcomes = [...new Set(allTests.map(test => test.outcome))].filter(outcome => outcome);
    
    // Create datasets for each outcome
    const datasets = uniqueOutcomes.map(outcome => {
      let color;
      switch(outcome) {
        case 'Passed':
          color = '#4caf50'; // Green
          break;
        case 'Failed':
          color = '#f44336'; // Red
          break;
        case 'Blocked':
          color = '#ff9800'; // Orange
          break;
        case 'Not Applicable':
          color = '#9e9e9e'; // Gray
          break;
        case 'Not Executed':
          color = '#2196f3'; // Blue
          break;
        default:
          color = '#9c27b0'; // Purple
      }
      
      const data = releases.map(release => {
        const tests = testData[release.version] || [];
        return tests.filter(test => test.outcome === outcome).length;
      });
      
      return {
        label: outcome,
        data,
        backgroundColor: color,
        borderColor: color,
        borderWidth: 1
      };
    });
    
    return {
      labels,
      datasets
    };
  };
  
  // Prepare chart data for automation test cases by status
  const prepareAutomationTestsData = () => {
    const labels = releases.map(release => release.version);
    
    // Get all possible outcomes
    const allTests = Object.values(testData).flat();
    const uniqueOutcomes = [...new Set(allTests.map(test => test.outcome))].filter(outcome => outcome);
    
    // Create datasets for each outcome
    const datasets = uniqueOutcomes.map(outcome => {
      let color;
      switch(outcome) {
        case 'Passed':
          color = '#4caf50'; // Green
          break;
        case 'Failed':
          color = '#f44336'; // Red
          break;
        case 'Blocked':
          color = '#ff9800'; // Orange
          break;
        case 'Not Applicable':
          color = '#9e9e9e'; // Gray
          break;
        case 'Not Executed':
          color = '#2196f3'; // Blue
          break;
        default:
          color = '#9c27b0'; // Purple
      }
      
      const data = releases.map(release => {
        const tests = testData[release.version] || [];
        return tests.filter(test => 
          test.outcome === outcome && 
          (test.automationStatus === 'Automated-UI' || test.automationStatus === 'Automated-API')
        ).length;
      });
      
      return {
        label: outcome,
        data,
        backgroundColor: color,
        borderColor: color,
        borderWidth: 1
      };
    });
    
    return {
      labels,
      datasets
    };
  };
  
  // Prepare chart data for overall test cases by status
  const prepareOverallTestsData = () => {
    const labels = releases.map(release => release.version);
    
    // Get all possible outcomes
    const allTests = Object.values(testData).flat();
    const uniqueOutcomes = [...new Set(allTests.map(test => test.outcome))].filter(outcome => outcome);
    
    // Create datasets for each outcome
    const datasets = uniqueOutcomes.map(outcome => {
      let color;
      switch(outcome) {
        case 'Passed':
          color = '#4caf50'; // Green
          break;
        case 'Failed':
          color = '#f44336'; // Red
          break;
        case 'Blocked':
          color = '#ff9800'; // Orange
          break;
        case 'Not Applicable':
          color = '#9e9e9e'; // Gray
          break;
        case 'Not Executed':
          color = '#2196f3'; // Blue
          break;
        default:
          color = '#9c27b0'; // Purple
      }
      
      const data = releases.map(release => {
        const tests = testData[release.version] || [];
        return tests.filter(test => test.outcome === outcome).length;
      });
      
      return {
        label: outcome,
        data,
        backgroundColor: color,
        borderColor: color,
        borderWidth: 1
      };
    });
    
    return {
      labels,
      datasets
    };
  };
  
  // Calculate days to release (excluding weekends)
  const calculateDaysToRelease = (releaseDate) => {
    if (!releaseDate) return null;
    
    const today = new Date();
    const release = new Date(releaseDate);
    
    // If release date is in the past, return 0
    if (release < today) return 0;
    
    let days = 0;
    const currentDate = new Date(today);
    currentDate.setHours(0, 0, 0, 0); // Reset time part
    
    while (currentDate < release) {
      const dayOfWeek = currentDate.getDay();
      // Skip weekends (0 = Sunday, 6 = Saturday)
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        days++;
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return days;
  };
  
  // Get the next upcoming release
  const upcomingRelease = useMemo(() => {
    if (!releases || releases.length === 0) return null;
    
    const today = new Date();
    const futureReleases = releases
      .filter(release => release.releaseDate && new Date(release.releaseDate) > today)
      .sort((a, b) => new Date(a.releaseDate) - new Date(b.releaseDate));
    
    return futureReleases.length > 0 ? futureReleases[0] : null;
  }, [releases]);
  
  // Calculate days to next release
  const daysToRelease = useMemo(() => {
    if (!upcomingRelease || !upcomingRelease.releaseDate) return null;
    return calculateDaysToRelease(upcomingRelease.releaseDate);
  }, [upcomingRelease]);
  
  // Chart options
  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      datalabels: {
        color: '#fff',
        anchor: 'center',
        align: 'center',
        formatter: (value) => value > 0 ? value : '',
        font: {
          weight: 'bold',
          size: 12
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Number of Tests'
        }
      }
    }
  };
  
  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Release Comparisons
      </Typography>
      
      {loading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : Object.keys(testData).length === 0 ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          No test data available for comparison. Please fetch test data for at least one release first.
        </Alert>
      ) : (
        <Grid container spacing={3}>
          {/* Charts Row - All three charts side by side */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Test Execution Charts
            </Typography>
            <Grid container spacing={3} sx={{ mb: 4 }}>
              {/* Execution Status Chart */}
              <Grid item xs={12} md={4}>
                <Paper elevation={2} sx={{ p: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Execution Status
                  </Typography>
                  <Box height="300px">
                    <Bar 
                      data={prepareExecutionStatusData()} 
                      options={{
                        ...barOptions,
                        plugins: {
                          ...barOptions.plugins,
                          datalabels: {
                            display: false
                          }
                        },
                        scales: {
                          ...barOptions.scales,
                          x: {
                            stacked: true,
                          },
                          y: {
                            ...barOptions.scales.y,
                            stacked: true,
                          }
                        }
                      }} 
                    />
                  </Box>
                </Paper>
              </Grid>
              
              {/* Total Tests Chart */}
              <Grid item xs={12} md={4}>
                <Paper elevation={2} sx={{ p: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Total Tests
                  </Typography>
                  <Box height="300px">
                    <Bar 
                      data={prepareOverallTestsData()} 
                      options={{
                        ...barOptions,
                        plugins: {
                          ...barOptions.plugins,
                          datalabels: {
                            display: false
                          }
                        },
                        scales: {
                          ...barOptions.scales,
                          x: {
                            stacked: true,
                          },
                          y: {
                            ...barOptions.scales.y,
                            stacked: true,
                          }
                        }
                      }} 
                    />
                  </Box>
                </Paper>
              </Grid>
              
              {/* Days to Release Infographic */}
              <Grid item xs={12} md={4}>
                <Paper elevation={2} sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Days to Next Release
                  </Typography>
                  {upcomingRelease ? (
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h1" sx={{ color: '#1976d2', fontWeight: 'bold', fontSize: '5rem', mb: 2 }}>
                        {daysToRelease !== null ? daysToRelease : 'N/A'}
                      </Typography>
                      <Typography variant="body1">
                        Next Release: {upcomingRelease.version}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Release Date: {upcomingRelease.releaseDate ? new Date(upcomingRelease.releaseDate).toLocaleDateString() : 'Not set'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                        *Excluding weekends
                      </Typography>
                    </Box>
                  ) : (
                    <Typography variant="body1" color="text.secondary">
                      No upcoming releases scheduled
                    </Typography>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </Grid>
          
          {/* Data Tables Row */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Test Data Tables
            </Typography>
            <Grid container spacing={3}>
              {/* Execution Status Table */}
              <Grid item xs={12} md={4}>
                <Paper elevation={2} sx={{ p: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Execution Status Data
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Release</TableCell>
                          {prepareExecutionStatusData().datasets.map(dataset => (
                            <TableCell key={dataset.label} align="right">{dataset.label}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {releases.map((release, releaseIndex) => (
                          <TableRow key={release.version}>
                            <TableCell>{release.version}</TableCell>
                            {prepareExecutionStatusData().datasets.map((dataset, datasetIndex) => (
                              <TableCell key={`${release.version}-${dataset.label}`} align="right">
                                {dataset.data[releaseIndex]}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Grid>
              
              {/* Total Tests Table */}
              <Grid item xs={12} md={4}>
                <Paper elevation={2} sx={{ p: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Total Tests
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Release</TableCell>
                          {prepareOverallTestsData().datasets.map(dataset => (
                            <TableCell key={dataset.label} align="right">{dataset.label}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {releases.map((release, releaseIndex) => (
                          <TableRow key={release.version}>
                            <TableCell>{release.version}</TableCell>
                            {prepareOverallTestsData().datasets.map((dataset, datasetIndex) => (
                              <TableCell key={`${release.version}-${dataset.label}`} align="right">
                                {dataset.data[releaseIndex]}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Grid>
              
              {/* Days to Release Table */}
              <Grid item xs={12} md={4}>
                <Paper elevation={2} sx={{ p: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Release Schedule Data
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Release</TableCell>
                          <TableCell align="right">Release Date</TableCell>
                          <TableCell align="right">Days Remaining</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {releases.filter(release => release.releaseDate).map((release) => {
                          const releaseDate = new Date(release.releaseDate);
                          const daysRemaining = calculateDaysToRelease(release.releaseDate);
                          return (
                            <TableRow key={release.version}>
                              <TableCell>{release.version}</TableCell>
                              <TableCell align="right">{releaseDate.toLocaleDateString()}</TableCell>
                              <TableCell align="right">{daysRemaining}</TableCell>
                            </TableRow>
                          );
                        })}
                        {releases.filter(release => release.releaseDate).length === 0 && (
                          <TableRow>
                            <TableCell colSpan={3} align="center">No release dates scheduled</TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default ReleaseComparisonCharts;
